import { apiFetch } from './client';

/**
 * FACTURACIÓN — `/api/facturacion/*`, del módulo `ventas`.
 *
 * La factura es el papel de una venta. Su estado no se guarda: sale de la
 * venta (cobrada → pagada, anulada → anulada) y de su fecha (vencida).
 *
 * Los montos que terminan en `Cent` van en CENTAVOS: son las cuentas fiscales
 * (base, ITBIS, retenciones). Los demás siguen en pesos enteros, como la venta.
 */

export type EstadoDeFactura = 'PENDIENTE' | 'VENCIDA' | 'PAGADA' | 'ANULADA';
export type TipoDeNcf = 'B01' | 'B02' | 'B14' | 'B15';

export interface LineaDeFactura {
  sku: string | null;
  descripcion: string;
  talla: string;
  cantidad: number;
  precio: number;
  descuento: number;
  importe: number;
  /** Falta en las facturas viejas: ahí todo iba gravado. */
  aplicaItbis?: boolean;
}

export interface Factura {
  id: string;
  numero: string;
  saleId: string;
  estado: EstadoDeFactura;
  clienteNombre: string;
  clienteDocumento: string | null;
  clienteTelefono: string | null;
  clienteDireccion: string | null;
  emisorJson: { nombre?: string; rnc?: string; direccion?: string; telefono?: string; pie?: string };
  lineasJson: LineaDeFactura[];
  subtotal: number;
  descuento: number;
  envio: number;
  itbisTasa: number;
  itbis: number;
  total: number;
  /** Base sin ITBIS (lo que va al 607). */
  montoCent: number;
  /** De esa base, lo que no lleva ITBIS. */
  exentoCent: number;
  itbisCent: number;
  brutoCent: number;
  retItbisCent: number;
  retIsrTasa: number;
  retIsrCent: number;
  /** Lo que de verdad se recibe: el total menos lo que retiene el cliente. */
  netoCent: number;
  vence: string | null;
  retConstancia: string | null;
  /** De qué lugar se despacha, como se llamaba el día que se emitió. */
  despachoDesde?: string | null;
  ncf: string | null;
  ncfTipo: TipoDeNcf | null;
  ncfVence: string | null;
  notas: string | null;
  emitidaAt: string;
  /** Lo que volvió después de emitida. */
  devuelto: number;
  sale: {
    id: string;
    numero: string;
    estado: string;
    cobrado: boolean;
    cobradoAt: string | null;
    anuladaAt: string | null;
    formaDePago: string | null;
    clienteId: string | null;
  };
}

/** Lo que puede elegir quien factura. Sin RNC ni secuencias. */
export interface EstadoDeFacturacion {
  /** Porcentaje; 0 = sin ITBIS. */
  itbis: number;
  /** Vacío = comprobantes fiscales apagados. */
  ncf: Array<{ tipo: TipoDeNcf; nombre: string; requiereRnc: boolean; quedan: number }>;
  diasDeVencimiento: number;
  tasasDeIsr: number[];
}

export interface Secuencia {
  tipo: TipoDeNcf;
  desde: number;
  hasta: number;
  siguiente: number;
  vence: string | null;
}

export interface ConfigDeFacturacion {
  emisor: { nombre: string; rnc: string; direccion: string; telefono: string };
  itbis: { activo: boolean; tasa: number };
  ncf: { activo: boolean; secuencias: Secuencia[] };
  pie: string;
  diasDeVencimiento: number;
}

export interface Retenciones {
  retItbis?: boolean;
  retIsr?: boolean;
  isrTasa?: number;
}

/** Las cuentas de una factura, en centavos. */
export interface CuentasDeFactura {
  itbisTasa: number;
  sub: number;
  baseGravada: number;
  exento: number;
  itbis: number;
  bruto: number;
  retItbis: number;
  retIsr: number;
  total: number;
}

export interface ResumenDgii {
  periodo: string;
  itbisTasa: number;
  montoFacturado: number;
  itbisFacturado: number;
  itbisCobrado: number;
  itbisRetenido: number;
  itbisEnGastos: number;
  neto: number;
  retItbis: number;
  retIsr: number;
  retencionesConConstancia: number;
  retencionesSinConstancia: number;
  en607: number;
  anuladasConNcf: Array<{ id: string; numero: string; ncf: string; fecha: string }>;
  facturas: Array<{
    id: string;
    numero: string;
    fecha: string;
    estado: EstadoDeFactura;
    clienteNombre: string;
    clienteDocumento: string | null;
    ncf: string | null;
    montoCent: number;
    itbisCent: number;
    brutoCent: number;
    retenido: number;
    retConstancia: string | null;
  /** De qué lugar se despacha, como se llamaba el día que se emitió. */
  despachoDesde?: string | null;
  }>;
}

export function listarFacturas(): Promise<Factura[]> {
  return apiFetch<Factura[]>('/api/facturacion/facturas');
}

export function leerFactura(id: string): Promise<Factura> {
  return apiFetch<Factura>(`/api/facturacion/facturas/${id}`);
}

export function emitirFactura(
  body: {
    saleId: string;
    clienteNombre?: string | null;
    clienteDocumento?: string | null;
    clienteTelefono?: string | null;
    clienteDireccion?: string | null;
    ncfTipo?: TipoDeNcf | null;
    notas?: string | null;
    vence?: string | null;
  } & Retenciones,
): Promise<Factura> {
  return apiFetch<Factura>('/api/facturacion/facturas', { method: 'POST', body });
}

export function previaDeFactura(saleId: string, ret: Retenciones = {}): Promise<CuentasDeFactura> {
  return apiFetch<CuentasDeFactura>('/api/facturacion/previa', {
    query: { saleId, retItbis: ret.retItbis ?? false, retIsr: ret.retIsr ?? false, isrTasa: ret.isrTasa ?? 10 },
  });
}

export function pagarFactura(id: string): Promise<Factura> {
  return apiFetch<Factura>(`/api/facturacion/facturas/${id}/pagar`, { method: 'POST' });
}

export function anularFactura(id: string, motivo: string): Promise<Factura> {
  return apiFetch<Factura>(`/api/facturacion/facturas/${id}/anular`, {
    method: 'POST',
    body: { motivo },
  });
}

export function anotarConstancia(id: string, numero: string | null): Promise<Factura> {
  return apiFetch<Factura>(`/api/facturacion/facturas/${id}/constancia`, {
    method: 'PUT',
    body: { numero },
  });
}

export function estadoDeFacturacion(): Promise<EstadoDeFacturacion> {
  return apiFetch<EstadoDeFacturacion>('/api/facturacion/estado');
}

export function resumenDgii(periodo: string): Promise<ResumenDgii> {
  return apiFetch<ResumenDgii>('/api/facturacion/dgii', { query: { periodo } });
}

export function exportar607(periodo: string): Promise<{ archivo: string; filas: number; csv: string }> {
  return apiFetch('/api/facturacion/dgii/607', { query: { periodo } });
}

export function leerConfig(): Promise<ConfigDeFacturacion> {
  return apiFetch<ConfigDeFacturacion>('/api/facturacion/config');
}

export function guardarConfig(body: ConfigDeFacturacion): Promise<ConfigDeFacturacion> {
  return apiFetch<ConfigDeFacturacion>('/api/facturacion/config', { method: 'PUT', body });
}
